import { useState, useEffect, createRef } from "react";
import { connect } from "react-redux";
import _get from "lodash/get";

import { getReviewsAndQuestions } from "@store/catalog/actions";
import ReviewList from "@containers/Reviews/ReviewList";
import SortFilter from "@containers/Reviews/SortFilter";
import Pagination from "@components/Pagination";
import { scrollElementIntoView } from "@lib/helpers";
import {
  getReviewByIdApi,
  getReviewsWithImagesApi,
  isUserEligibleToPostReviewApi,
} from "@lib/api";
import ProductReviewsChart from "@components/ProductReviews/ProductReviewsChart";
import ProductReviewsInfo from "@components/ProductReviews/ProductReviewsInfo";
import AddReviewForm from "@containers/Reviews/AddReviewForm";
import ProductReviewsFormPlaceholder from "@components/ProductReviews/ProductReviewsFormPlaceholder";
import { addModal, apiCall } from "@store/app/actions";
import Loading from "@components/Loading";
import Button from "@components/Button";
import ReviewsImagesSlider from "@components/ProductReviews/ReviewsImagesSlider";
import ModalErrorBoundary from "@containers/ModalErrorBoundary";
import ProductReviewDetailsModal from "@components/ProductReviews/ProductReviewDetailsModal";
import ProductReviewsImagesModal from "@components/ProductReviews/ProductReviewsImagesModal";

const ReviewsContainer = ({
  user,
  product,
  type,
  reviewsSummary,
  apiCall,
  getReviewsAndQuestions,
  addModal,
  openedModal,
}) => {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchReviews, setFetchReviews] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [placeholder, setPlaceholder] = useState(false);
  const [placeholderLabel, setPlaceholderLabel] = useState(null);
  const [sortFilterData, setSortFilterData] = useState({
    sortBy: "date",
    orderBy: "desc",
  });
  const [filtered, setFiltered] = useState(false);
  const [reviewsWithImages, setReviewsWithImages] = useState();
  const [reviewDetails, setReviewDetails] = useState();
  const [currentImageIndex, setSelectedImageIndex] = useState();

  const scrollRef = createRef();

  const reviewsImagesCount =
    reviewsWithImages &&
    reviewsWithImages.flatMap((review) => [...review.images]).length;

  const reviewsHasImages = type === "reviews" && reviewsWithImages?.length > 0;

  let reviewsCount = _get(reviewsSummary, "reviews.value", 0);
  let questionsCount = _get(reviewsSummary, "questions.total", 0);

  const productVariations =
    product?.configurable_options
      ?.reduce((accumulator, { values }) => accumulator.concat(values), [])
      .reduce((accumulator, { sku, label }) => {
        accumulator[sku] ??= [];
        accumulator[sku].push(label);
        return accumulator;
      }, {}) ?? null; // Default to null in case product has no variations

  // TODO: why is this needed? if reviewsSummary changes the component rerenders
  useEffect(() => {
    reviewsCount = _get(reviewsSummary, "reviews.value", 0);
    questionsCount = _get(reviewsSummary, "questions.total", 0);
  }, [reviewsSummary]);

  useEffect(() => getByPage(0), [sortFilterData, filtered, user.isLoggedIn]); // ? Maybe without filter?

  useEffect(() => {
    const fetchReviewsWithImages = async () => {
      let payload = [product.sku];

      if (productVariations) {
        payload = Object.keys(productVariations);
      }

      const response = await apiCall({
        apiArgs: [payload],
        apiCall: getReviewsWithImagesApi,
        returnResponse: true,
        formatResponse: (response) => response.data,
      });

      if (response?.items?.length > 0) {
        setReviewsWithImages(response.items);
      }
    };

    if (type === "reviews") {
      fetchReviewsWithImages();
    }
  }, []);

  const handleReviewDetailsModal = ({ reviewId, imageIndex }) => {
    setSelectedImageIndex(imageIndex);
    handleReviewDetails(reviewId);

    if (openedModal !== "review-images-details") {
      addModal("review-images-details");
    }
  };

  const handleReviewDetails = async (reviewId) => {
    const findReviewById = (review) => review._id === reviewId;

    // this could be the current, next or previous review
    const review = reviewsWithImages.find(findReviewById);
    // Check if review have other data aside from id and images
    const hasReviewData = Boolean(review.body && review.title);

    if (hasReviewData) {
      setReviewDetails(review);
    } else {
      const reviewData = await apiCall({
        apiArgs: [reviewId],
        apiCall: getReviewByIdApi,
        formatResponse: (response) => response.data,
        returnResponse: true,
      });

      if (reviewData) {
        const newReviewsWithImages = [...reviewsWithImages];
        const reviewIndex = newReviewsWithImages.findIndex(findReviewById);
        // populate review with data to avoid future api call
        newReviewsWithImages[reviewIndex] = reviewData;

        setReviewsWithImages(newReviewsWithImages);
        setReviewDetails(reviewData);
      }
    }
  };

  const handleNextImage = () => {
    // zero based images count to match current image index
    const currentReviewImagesCount = reviewDetails.images.length - 1;
    const nextImageIndex = currentImageIndex + 1;

    // if is not the last image in current review, show next image
    if (nextImageIndex <= currentReviewImagesCount) {
      setSelectedImageIndex(nextImageIndex);
    } else {
      const currentReviewIndex = reviewsWithImages.indexOf(reviewDetails);
      const nextReviewId = reviewsWithImages[currentReviewIndex + 1]?._id;
      // if is last image in review and next review is available, get next review in modal
      if (nextReviewId) {
        setSelectedImageIndex(0);
        handleReviewDetails(nextReviewId);
      }
    }
  };

  const handlePrevImage = () => {
    const prevImageIndex = currentImageIndex - 1;

    if (prevImageIndex >= 0) {
      setSelectedImageIndex(prevImageIndex);
    } else {
      const currentReviewIndex = reviewsWithImages.indexOf(reviewDetails);
      const prevReviewId = reviewsWithImages[currentReviewIndex - 1]?._id;
      // zero based images count to match current image index
      const prevReviewImagesCount =
        reviewsWithImages[currentReviewIndex - 1]?.images.length - 1;

      if (prevReviewId) {
        setSelectedImageIndex(prevReviewImagesCount);
        handleReviewDetails(prevReviewId);
      }
    }
  };

  const userCanPostQuestion = () => {
    if (user.isLoggedIn) {
      setVisibleForm(true);
      setPlaceholder(false);
    } else {
      setVisibleForm(false);
      setPlaceholder(true);
    }
  };

  const userCanPostReview = async () => {
    const { id, sku } = product;
    // avoid call if user is not logged in
    if (!user.isLoggedIn) {
      setVisibleForm(false);
      setPlaceholder(true);
      return;
    }
    setLoading(true);

    await apiCall({
      apiArgs: [{ productId: id, sku }],
      apiCall: isUserEligibleToPostReviewApi,
      formatResponse: (response) => {
        if (response.status === 200 && !!response.data.is_eligible) {
          setVisibleForm(true);
          setPlaceholder(false);
          setLoading(false);
          setPlaceholderLabel("");
        }
      },
      preFail: (response) => {
        if (response.status === 409) {
          setVisibleForm(false);
          setPlaceholder(true);
          setLoading(false);
          setPlaceholderLabel(response.data.message);
        } else {
          setVisibleForm(false);
          setPlaceholder(true);
          setLoading(false);
          setPlaceholderLabel(
            "Serviciu indisponibil momentan. Va rugam reveniti."
          );
        }
      },
      returnResponse: true,
    });
  };

  const getByPage = async (page, options = {}) => {
    const { scroll = false } = options;

    let elem = scrollRef.current;
    setFetchReviews(true);

    const productSkus = productVariations
      ? Object.keys(productVariations)
      : [product.sku]; // Use current product SKU for the review API call

    const payload = {
      page,
      productSkus,
      ...sortFilterData,
    };

    const data = await getReviewsAndQuestions(type, payload);

    if (data) {
      setData(data.items || []);
      setMeta(data.meta || null);
      setFetchReviews(false);

      if (scroll && data?.items?.length > 0) {
        scrollElementIntoView(elem, "smooth");
      }

      // getByPage ia called when tab is active
      // we only call funtion bellow if product has no reviews or questions
      if (type === "reviews" && _get(data, "meta.size", 0) < 1) {
        userCanPostReview();
      }

      if (type === "questions" && !data.items.length) {
        userCanPostQuestion();
      }
    }
  };

  const handleEditReview = () => {
    getByPage(0);
  };

  const changeSort = async (payload) => {
    setSortFilterData({ ...payload });
    setFiltered(true);
  };

  const closeForm = () => {
    setVisibleForm(false);
  };

  return (
    <div className="md:mb-16" ref={scrollRef}>
      {type === "reviews" && data.length > 0 && (
        <div className="flex flex-col md:flex-row items-center md:mb-6">
          <ProductReviewsInfo
            reviewsSummary={reviewsSummary}
            visibleForm={visibleForm}
            placeholder={placeholder}
            userCanPostReview={userCanPostReview}
          />
          {reviewsSummary &&
            reviewsSummary.ratingDistribution &&
            Object.keys(reviewsSummary.ratingDistribution).length !== 0 && (
              <div className="md:ml-52px w-full max-w-[525px]">
                <ProductReviewsChart
                  ratingDistribution={reviewsSummary.ratingDistribution}
                  reviewsCount={reviewsSummary.reviews.value || 0}
                  changeRatingFilter={changeSort}
                  sortFilterData={sortFilterData}
                  hasRatingFilters={
                    Object.keys(reviewsSummary.ratingDistribution).length !== 1
                  }
                />
              </div>
            )}
        </div>
      )}

      {data && data.length > 0 && type === "questions" && (
        <div className="py-4 lg:pt-8 md:block md:w-190px">
          <Button
            title="Adauga o intrebare"
            display="block"
            disabled={visibleForm}
            size="normal"
            onClick={() => userCanPostQuestion()}
          />
        </div>
      )}

      {reviewsCount > 0 && !fetchReviews && !loading && (
        <div className="md:hidden my-4">
          <Button
            title="Adauga recenzia ta"
            disabled={visibleForm || placeholder}
            display="block"
            size="normal"
            onClick={() => userCanPostReview()}
          />
        </div>
      )}

      {loading && type === "reviews" && <Loading height={100} />}

      {!user.isLoggedIn && placeholder && (
        <ProductReviewsFormPlaceholder
          addModal={addModal}
          type={type}
          showLogin={!user.isLoggedIn}
          label={
            user.isLoggedIn && placeholder
              ? placeholderLabel
              : `Pentru a adauga o
              ${type === "reviews" ? " recenzie" : " intrebare"}
               acestui produs trebuie sa fii logat.`
          }
        />
      )}

      {user.isLoggedIn && !loading && (
        <AddReviewForm
          type={type}
          visibleForm={visibleForm}
          placeholder={placeholder}
          placeholder_label={placeholderLabel}
          closeForm={closeForm}
          productId={product.id}
          sku={product.sku}
          counter={
            type === "reviews" && reviewsCount === 0
              ? true
              : type !== "reviews" && questionsCount === 0
              ? true
              : false
          }
        />
      )}

      {reviewsHasImages && (
        <>
          <div className="text-[18px] md:text-[24px]">Recenzii cu imagini</div>

          <ReviewsImagesSlider
            reviewsWithImages={reviewsWithImages}
            onImageSelect={handleReviewDetailsModal}
          />

          {reviewsImagesCount && (
            <button
              onClick={() => addModal("reviews-images-modal")}
              className="text-red-brand text-[14px] mt-[15px] cursor-pointer hover:text-bleu-hover"
            >
              Vezi toate fotografiile ({reviewsImagesCount})
            </button>
          )}
        </>
      )}

      {((meta && meta.size > meta.limit) || filtered) && (
        <SortFilter
          type={type}
          reviewsSummary={reviewsSummary}
          sortFilterData={sortFilterData}
          changeSort={changeSort}
        />
      )}

      {!(data && data.length > 0) && filtered && (
        <div className="my-4 md:py-8 px-4 md:bg-newGray text-center text-black">
          {type === "reviews" ? (
            <p className="m-0 mb-2">Nu a fost gasita nicio recenzie.</p>
          ) : (
            <p className="m-0 mb-2">Nu a fost gasita nicio intrebare.</p>
          )}
        </div>
      )}

      <ReviewList
        loading={fetchReviews}
        reviews={data}
        reviewsSummary={reviewsSummary}
        type={type}
        productId={product.id}
        productVariations={productVariations}
        onEditReview={handleEditReview}
      />

      {meta && meta.size > meta.limit && (
        <div className="md:flex justify-end">
          <div className="w-full md:w-5/6 pl-0 md:pl-8 mt-10px mb-12 md:mb-0 md:mt-0">
            <Pagination
              pages={
                Number.isInteger(meta.size / meta.limit)
                  ? meta.size / meta.limit
                  : Math.trunc(meta.size / meta.limit + 1)
              }
              goTo={(pg) => getByPage(pg - 1, { scroll: true })}
              activePage={meta.page + 1}
              meta={meta}
              showMeta={true}
              alignment="justify-center"
            />
          </div>
        </div>
      )}

      {reviewsHasImages && (
        <ModalErrorBoundary>
          <ProductReviewsImagesModal
            reviewsWithImages={reviewsWithImages}
            onImageSelect={handleReviewDetailsModal}
          />
        </ModalErrorBoundary>
      )}

      {reviewsHasImages && (
        <ModalErrorBoundary>
          <ProductReviewDetailsModal
            reviewsImagesCount={reviewsImagesCount}
            reviewDetails={reviewDetails}
            productVariations={productVariations}
            onPrevImageChange={handlePrevImage}
            onNextImageChange={handleNextImage}
            currentImageIndex={currentImageIndex}
          />
        </ModalErrorBoundary>
      )}
    </div>
  );
};

export default connect(
  (state) => ({
    user: state.user,
    openedModal: state.app.modal,
  }),
  { getReviewsAndQuestions, addModal, apiCall }
)(ReviewsContainer);
